/**
 * Standardized Rework Constants
 *
 * Shared enums for all rework tracking workflows:
 * - Time Ticket (REWORK/REVISION operations)
 * - Track Rework button
 * - Reprint Needed button
 */

/**
 * WHAT - Type of rework needed
 * Describes what work needs to be redone
 */
export const REWORK_TYPES = [
  { value: 'reprint_only', label: 'Reprint Only', description: 'Just reprinting, no other work' },
  { value: 'reprint_opti', label: 'Reprint + Opti', description: 'Reprint and re-apply Opti finish' },
  { value: 'reprint_opti_assembly', label: 'Reprint + Opti + Assembly', description: 'Full redo: print, Opti, and assembly' },
  { value: 'rework_art', label: 'Rework Art Files', description: 'Fix artwork/design files' },
  { value: 'recut', label: 'Re-cut / Re-finish', description: 'Re-cut or re-apply finishing' },
  { value: 'reassembly', label: 'Re-assembly Only', description: 'Only assembly work needs redone' },
  { value: 'paint_redo', label: 'Paint Redo', description: 'Redo paint work' },
  { value: '3d_reprint', label: '3D Print Redo', description: 'Reprint 3D model' },
  { value: 'other', label: 'Other', description: 'Other type of rework' },
];

/**
 * CAUSE - Root cause of the rework
 * Describes why the rework is needed
 */
export const REWORK_CAUSES = [
  // Human Error
  { value: 'pa_error', label: 'PA Manual Error', category: 'human', description: 'Production Art made a mistake' },
  { value: 'print_op_error', label: 'Print Operator Error', category: 'human', description: 'Printer operator made a mistake' },
  { value: 'assembly_error', label: 'Assembly Error', category: 'human', description: 'Assembly team made a mistake' },
  { value: 'cut_error', label: 'Cutting Error', category: 'human', description: 'Cutting/finishing mistake' },

  // Equipment
  { value: 'headstrike', label: 'Printer Headstrike', category: 'equipment', description: 'Print head struck the material' },
  { value: 'printer_malfunction', label: 'Printer Malfunction', category: 'equipment', description: 'Printer equipment failure' },
  { value: 'cutter_issue', label: 'Cutter Issue', category: 'equipment', description: 'Cutting equipment problem' },

  // Materials
  { value: 'material_defect', label: 'Material Defect', category: 'material', description: 'Defective material from supplier' },
  { value: 'wrong_material', label: 'Wrong Material Used', category: 'material', description: 'Incorrect material was used' },

  // External
  { value: 'client_change', label: 'Client Change Order', category: 'external', description: 'Client requested changes' },
  { value: 'damage_shipping', label: 'Shipping Damage', category: 'external', description: 'Damaged during shipping' },
  { value: 'damage_handling', label: 'Handling Damage', category: 'external', description: 'Damaged during handling' },

  // Process
  { value: 'miscommunication', label: 'Miscommunication', category: 'process', description: 'Communication breakdown' },
  { value: 'spec_unclear', label: 'Spec Was Unclear', category: 'process', description: 'Specifications were ambiguous' },

  { value: 'other', label: 'Other', category: 'other', description: 'Other cause' },
];

/**
 * Cause categories for grouping in reports
 */
export const CAUSE_CATEGORIES = {
  human: { label: 'Human Error', color: 'red' },
  equipment: { label: 'Equipment', color: 'orange' },
  material: { label: 'Material', color: 'yellow' },
  external: { label: 'External', color: 'blue' },
  process: { label: 'Process', color: 'purple' },
  other: { label: 'Other', color: 'gray' },
};

/**
 * Helper to get causes grouped by category
 */
export function getCausesByCategory() {
  const grouped = {};
  REWORK_CAUSES.forEach(cause => {
    if (!grouped[cause.category]) {
      grouped[cause.category] = {
        ...CAUSE_CATEGORIES[cause.category],
        causes: [],
      };
    }
    grouped[cause.category].causes.push(cause);
  });
  return grouped;
}

/**
 * Helper to find a rework type by value
 */
export function getReworkType(value) {
  return REWORK_TYPES.find(t => t.value === value);
}

/**
 * Helper to find a cause by value
 */
export function getReworkCause(value) {
  return REWORK_CAUSES.find(c => c.value === value);
}

/**
 * Time estimate options for non-time-ticket rework
 */
export const TIME_ESTIMATES = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '1 day (8 hours)' },
  { value: 0, label: 'Unknown / TBD' },
];
